import React, { Fragment, useEffect, useState } from 'react';
import { FormikValues } from 'formik';
import { makeStyles, TextField, Theme, Tooltip, Box } from '@material-ui/core';
import { Warning } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { TableRowWrapper } from './TableRowWrapper';
import { CapitalizeNames } from 'src/utils/capitalize';
import { AreaCodeTextField } from 'src/modules/forms/fields/AreaCodeTextField/AreaCodeTextField';
import useLead from 'src/hooks/useLead';

interface ILeadInfoForm {
    values: FormikValues;
    handleChange: Function;
    handleBlur: Function;
    touched: any;
    errors: any;
    handleSubmit: Function;
    setFieldValue: Function;
    loading: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
    input: {
        fontSize: '8px',
        '& .MuiInputBase-input': {
            color: theme.palette.text.secondary,
            fontSize: '16px',
        },
        '& .MuiInput-underline:before': {
            borderBottom: 'none',
        },
        '& .MuiInput-underline:hover:before': {
            borderBottom: 'none',
        },
        '& .MuiInput-underline:after': {
            borderBottom: 'none',
        },
    },
    warningIcon: {
        color: theme.palette.warning.main,
        marginRight: theme.spacing(1),
        fontSize: '20px',
    },
    emailContainer: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
    },
}));

export const LeadInfoForm = ({
    values,
    handleChange,
    handleBlur,
    touched,
    errors,
    handleSubmit,
    setFieldValue,
    loading,
}: ILeadInfoForm) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const { lead } = useLead();
    const [emailVerification, setEmailVerification] = useState(true);

    useEffect(() => {
        setEmailVerification(lead?.isEmailVerified);
    }, [lead?.isEmailVerified]);

    return (
        <Fragment>
            <TableRowWrapper
                header={t('Leads.Name')}
                loading={loading}
                handleSubmit={handleSubmit}
            >
                <TextField
                    disabled={loading}
                    value={CapitalizeNames(values?.name || '')}
                    error={Boolean(touched.name && errors.name)}
                    fullWidth
                    helperText={touched.name && errors.name}
                    autoComplete="off"
                    name="name"
                    onChange={(e) => {
                        handleChange(e);
                        handleBlur(e);
                    }}
                    className={classes.input}
                    inputProps={{
                        'aria-label': 'naked',
                    }}
                />
            </TableRowWrapper>
            <TableRowWrapper
                header={t('Leads.Email')}
                loading={loading}
                handleSubmit={handleSubmit}
            >
                <Box className={classes.emailContainer}>
                    {!emailVerification && (
                        <Tooltip title={t('Leads.EmailNotValid')}>
                            <Warning className={classes.warningIcon} />
                        </Tooltip>
                    )}
                    <TextField
                        value={values?.email || ''}
                        autoComplete="off"
                        error={Boolean(touched.email && errors.email)}
                        fullWidth
                        helperText={touched.email && errors.email}
                        name="email"
                        className={classes.input}
                        onChange={(e) => {
                            handleChange(e);
                            handleBlur(e);
                        }}
                    />
                </Box>
            </TableRowWrapper>
            <TableRowWrapper
                header={t('Leads.Phone')}
                loading={loading}
                handleSubmit={handleSubmit}
            >
                <AreaCodeTextField
                    values={values}
                    name="phone"
                    touched={touched}
                    errors={errors}
                    handleChange={handleChange}
                    setFieldValue={setFieldValue}
                    props={{
                        className: classes.input,
                        inputProps: {
                            'aria-label': 'naked',
                            variant: 'default',
                            label: '',
                        },
                    }}
                />
            </TableRowWrapper>
        </Fragment>
    );
};
